import { useDeviceSettings } from '@/composables/settings'
import wheelSpinningUrl from '@/assets/sounds/wheel_is_spinning-D3u6qQYl.mp3'
import cardUpgradedUrl from '@/assets/sounds/card-upgraded.mp3'
import purchaseSuccessUrl from '@/assets/sounds/purchase-success.mp3'
import mysteryBoxUrl from '@/assets/sounds/mystery-box.mp3'
import bonusActivatedUrl from '@/assets/sounds/bonus-activated.mp3'
import pathwayCompletedUrl from '@/assets/sounds/pathway-completed.mp3'

export enum GameSound {
  WHEEL_SPINNING = 'wheel_spinning',
  UPGRADE_SUCCESS = 'upgrade_success',
  PURCHASE_SUCCESS = 'purchase_success',
  MYSTERY_BOX = 'mystery_box',
  BONUS_ACTIVATED = 'bonus_activated',
  PATHWAY_COMPLETED = 'pathway_completed',
}

const SOUND_FILES: Record<GameSound, string> = {
  [GameSound.WHEEL_SPINNING]: wheelSpinningUrl,
  [GameSound.UPGRADE_SUCCESS]: cardUpgradedUrl,
  [GameSound.PURCHASE_SUCCESS]: purchaseSuccessUrl,
  [GameSound.MYSTERY_BOX]: mysteryBoxUrl,
  [GameSound.BONUS_ACTIVATED]: bonusActivatedUrl,
  [GameSound.PATHWAY_COMPLETED]: pathwayCompletedUrl,
}

const SOUND_VOLUMES: Record<GameSound, number> = {
  [GameSound.WHEEL_SPINNING]: 0.3,
  [GameSound.UPGRADE_SUCCESS]: 0.5,
  [GameSound.PURCHASE_SUCCESS]: 0.5,
  [GameSound.MYSTERY_BOX]: 0.4,
  [GameSound.BONUS_ACTIVATED]: 0.7,
  [GameSound.PATHWAY_COMPLETED]: 0.7,
}

const audioCache = new Map<GameSound, HTMLAudioElement>()

function getOrCreateAudio(sound: GameSound): HTMLAudioElement {
  let audio = audioCache.get(sound)
  if (!audio) {
    audio = new Audio(SOUND_FILES[sound])
    audio.volume = SOUND_VOLUMES[sound]
    audioCache.set(sound, audio)
  }
  return audio
}

let visibilityListenerAdded = false

function ensureVisibilityListener() {
  if (visibilityListenerAdded) return
  visibilityListenerAdded = true
  document.addEventListener('visibilitychange', () => {
    const muted = document.hidden
    audioCache.forEach((audio) => {
      audio.muted = muted
    })
  })
}

export const useSounds = () => {
  const { gameDeviceSettings } = useDeviceSettings()

  ensureVisibilityListener()

  function playSound(sound: GameSound, options?: { volume?: number }) {
    if (!gameDeviceSettings.value.soundEffects) return
    const audio = getOrCreateAudio(sound)
    audio.volume = options?.volume ?? SOUND_VOLUMES[sound]
    audio.currentTime = 0
    audio.play().catch(() => { /* autoplay may be blocked */ })
  }

  function stopSound(sound: GameSound) {
    const audio = audioCache.get(sound)
    if (audio) {
      audio.pause()
      audio.currentTime = 0
    }
  }

  function preloadSounds(sounds: GameSound[]) {
    sounds.forEach(getOrCreateAudio)
  }

  return {
    playSound,
    stopSound,
    preloadSounds,
  }
}
